import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage, isVersionConflict } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { formatDateTime } from "../../lib/missions";
import { HISTORY_LABELS, ISSUE_ACTIONS, ISSUE_STATUS_META, SEVERITIES, SEVERITY_META, formatDay, issueChanges, looksLikeSecret } from "../../lib/equipment";
import TaskFormSheet from "../TaskFormSheet";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertTriangle, ChevronDown, ListChecks, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

export function SeverityBadge({ severity }) {
  const meta = SEVERITY_META[severity];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${meta.className}`}>{meta.label}</span>;
}

export function IssueStatusBadge({ status }) {
  const meta = ISSUE_STATUS_META[status];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${meta.className}`}>{meta.label}</span>;
}

function Row({ label, children }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 break-words whitespace-pre-line">{children}</dd>
    </div>
  );
}

// Right-hand drawer of an equipment issue (same convention as TaskDetailSheet): what, where, who, until when, what was done;
// status changes, editing and the follow-up task when the server says so (`capabilities`). `issueId` null = closed.
export default function IssueDetailSheet({ issueId, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [issue, setIssue] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);
  const [editing, setEditing] = useState(false);
  const [followUp, setFollowUp] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    if (!issueId) return;
    try {
      const { data } = await api.get(`/equipment-issues/${issueId}`);
      setIssue(data);
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger l'anomalie."));
    }
  }, [issueId]);
  useEffect(() => { setIssue(null); setError(""); setEditing(false); setHistoryOpen(false); load(); }, [load]);

  // Stable object: TaskFormSheet resets its form whenever `initialValues` changes.
  const followUpValues = useMemo(() => issue && ({
    title: `Matériel : ${issue.title}`.slice(0, 200), priority: issue.severity === "critical" ? "critical" : "high", domain: "infrastructure",
    due_date: issue.due_date || "",
    description: [`Pharmacie : ${issue.pharmacy_name}`, `Anomalie : ${issue.number}`, issue.asset_label && `Équipement : ${issue.asset_label}`,
      issue.symptoms && `Symptômes : ${issue.symptoms}`].filter(Boolean).join("\n"),
  }), [issue]);
  const applied = (data, message) => { setIssue(data); setAction(null); setEditing(false); onChanged?.(data); if (message) toast.success(message); };
  const caps = issue?.capabilities || {};

  return (
    <Sheet open={!!issueId} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg p-4 sm:p-6 overflow-y-auto [&>button]:h-10 [&>button]:w-10 [&>button]:right-2 [&>button]:top-2 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
        data-testid="equipment-issue-sheet">
        {!issue ? (
          <>
          <SheetTitle className="sr-only">Anomalie matérielle</SheetTitle>
          {error ? (
            <div className="pt-8 space-y-3 text-sm text-slate-600"><p>{error}</p><Button variant="outline" size="sm" onClick={load}>Réessayer</Button></div>
          ) : <div className="pt-8 space-y-3"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-24 w-full" /></div>}
          </>
        ) : (
          <div className="space-y-5">
            <SheetHeader className="text-left pr-8">
              <p className="text-xs text-slate-500" data-testid="equipment-issue-number">{issue.number} · {issue.pharmacy_name}</p>
              <SheetTitle className="break-words">{issue.title}</SheetTitle>
              <SheetDescription className="flex flex-wrap gap-1.5 pt-1">
                <SeverityBadge severity={issue.severity} /><IssueStatusBadge status={issue.status} />
                {issue.overdue && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-2xs font-medium text-red-700"><AlertTriangle size={11} />En retard</span>}
              </SheetDescription>
            </SheetHeader>

            {editing ? (
              <EditForm issue={issue} onCancel={() => setEditing(false)} onDone={(d) => applied(d, "Anomalie modifiée")} onConflict={load} />
            ) : (
              <dl className="space-y-2.5" data-testid="equipment-issue-fields">
                <Row label="Équipement">{issue.asset_label || "Aucun en particulier"}</Row>
                <Row label="Responsable">{issue.responsible_name}</Row>
                <Row label="Échéance">{issue.due_date ? formatDay(issue.due_date) : "—"}</Row>
                {issue.mission_reference && <Row label="Mission"><Link className="text-jamora hover:underline" to={`/missions/${issue.mission_id}`}>{issue.mission_reference}</Link></Row>}
                {issue.symptoms && <Row label="Symptômes">{issue.symptoms}</Row>}
                {issue.impact && <Row label="Impact">{issue.impact}</Row>}
                {issue.attempted_action && <Row label="Action tentée">{issue.attempted_action}</Row>}
                {issue.resolution && <Row label="Résolution">{issue.resolution}</Row>}
                {issue.abandon_reason && <Row label="Motif d'abandon">{issue.abandon_reason}</Row>}
                <Row label="Ouverte">{formatDateTime(issue.created_at)} par {issue.created_by_name}</Row>
                {issue.closed_at && <Row label="Close">{formatDateTime(issue.closed_at)} par {issue.closed_by_name}</Row>}
              </dl>
            )}

            {!editing && (
              <div className="space-y-2" data-testid="equipment-issue-follow-up">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><ListChecks size={14} className="text-slate-400" />Tâche de suivi</h4>
                {issue.follow_up_task ? (
                  <p className="text-sm text-slate-700">{issue.follow_up_task.visible ? issue.follow_up_task.title : "Une tâche existe (vous ne la voyez pas)."}</p>
                ) : caps.follow_up ? (
                  <Button variant="outline" size="sm" onClick={() => setFollowUp(true)} data-testid="equipment-issue-follow-up-create">Créer une tâche de suivi</Button>
                ) : <p className="text-sm text-slate-500">Aucune.</p>}
              </div>
            )}

            {caps.edit && !editing && (
              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4" data-testid="equipment-issue-actions">
                {(ISSUE_ACTIONS[issue.status] || []).map((a, i) => (
                  <Button key={a.to} variant={i === 0 ? "default" : "outline"} size="sm" onClick={() => setAction(a)} data-testid={`equipment-issue-to-${a.to}`}>{a.label}</Button>
                ))}
                {["open", "in_progress"].includes(issue.status) && (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="equipment-issue-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>
                )}
              </div>
            )}

            <div>
              <button type="button" onClick={() => setHistoryOpen((v) => !v)} aria-expanded={historyOpen} className="text-sm font-medium text-slate-900 inline-flex items-center gap-1"
                data-testid="equipment-issue-history-toggle">
                <ChevronDown size={14} className={historyOpen ? "rotate-180" : ""} />Historique ({issue.history.length})
              </button>
              {historyOpen && (
                <ol className="mt-2 space-y-2" data-testid="equipment-issue-history">
                  {issue.history.map((h) => (
                    <li key={h.id} className="text-sm">
                      <div className="text-slate-800">{HISTORY_LABELS[h.kind] || "Modification"}{h.reason ? ` — ${h.reason}` : ""}</div>
                      <div className="text-xs text-slate-500">{formatDateTime(h.at)} · {h.actor_name}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </SheetContent>
      {issue && <StatusDialog issue={issue} action={action} onClose={() => setAction(null)} onDone={(d) => applied(d, `Anomalie : ${ISSUE_STATUS_META[d.status].label.toLowerCase()}`)} onConflict={load} />}
      {issue && (
        <TaskFormSheet open={followUp} onOpenChange={setFollowUp} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false} directory={[]}
          initialValues={followUpValues}
          onSaved={async (task) => {
            setFollowUp(false);
            try {
              const { data } = await api.post(`/equipment-issues/${issue.id}/follow-up`, { task_id: task.id, expected_version: issue.version });
              applied(data, "Tâche de suivi reliée à l'anomalie");
            } catch (e) {
              toast.error(errorMessage(e, "La tâche est créée mais n'a pas pu être reliée à l'anomalie."));
              load();
            }
          }} />
      )}
    </Sheet>
  );
}

function StatusDialog({ issue, action, onClose, onDone, onConflict }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setText(""); setError(""); }, [action]);
  const needs = action?.resolution ? "resolution" : action?.reason ? "reason" : null;
  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { status: action.to, expected_version: issue.version };
      if (needs) body[needs] = text.trim();
      const { data } = await api.patch(`/equipment-issues/${issue.id}`, body);
      onDone(data);
    } catch (e) {
      setError(errorMessage(e, "Changement impossible. Réessayez."));
      if (isVersionConflict(e)) onConflict();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!action} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="equipment-issue-status-dialog">
        <DialogHeader>
          <DialogTitle>{action?.label} l'anomalie {issue.number} ?</DialogTitle>
          <DialogDescription>{needs === "resolution" ? "Décrivez ce qui a réglé le problème." : needs === "reason" ? "Le motif reste dans l'historique." : "Le changement est enregistré dans l'historique."}</DialogDescription>
        </DialogHeader>
        {needs && (
          <div className="space-y-1.5">
            <Label htmlFor="eq-status-text">{needs === "resolution" ? "Résolution *" : "Motif *"}</Label>
            <Textarea id="eq-status-text" rows={3} maxLength={needs === "resolution" ? 1000 : 300} className="text-base md:text-sm" value={text} onChange={(e) => setText(e.target.value)}
              data-testid="equipment-issue-status-text" />
            {looksLikeSecret(text) && <p className="text-xs text-amber-800">Ne notez jamais de mot de passe, code ou clé Wi-Fi.</p>}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={send} disabled={busy || (needs && text.trim().length < 3)} data-testid="equipment-issue-status-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{action?.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditForm({ issue, onCancel, onDone, onConflict }) {
  const [form, setForm] = useState(() => ({ title: issue.title, severity: issue.severity, symptoms: issue.symptoms || "", impact: issue.impact || "",
    attempted_action: issue.attempted_action || "", responsible_user_id: issue.responsible_user_id, due_date: issue.due_date || "" }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const changes = issueChanges(issue, form);
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(`/equipment-issues/${issue.id}`, { ...changes, expected_version: issue.version });
      onDone(data);
    } catch (e) {
      setError(errorMessage(e, "Enregistrement impossible. Réessayez."));
      if (isVersionConflict(e)) onConflict();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="equipment-issue-edit-form">
      <div className="space-y-1.5"><Label htmlFor="eq-edit-title">Titre</Label>
        <Input id="eq-edit-title" className="text-base md:text-sm" maxLength={120} value={form.title} onChange={set("title")} data-testid="equipment-issue-edit-title" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Gravité</Label>
          <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
            <SelectTrigger data-testid="equipment-issue-edit-severity"><SelectValue /></SelectTrigger>
            <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{SEVERITY_META[s].label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label htmlFor="eq-edit-due">Échéance</Label>
          <Input id="eq-edit-due" type="date" className="text-base md:text-sm" value={form.due_date} onChange={set("due_date")} data-testid="equipment-issue-edit-due" /></div>
      </div>
      {issue.responsible_candidates.length > 0 && (
        <div className="space-y-1.5"><Label>Responsable</Label>
          <Select value={form.responsible_user_id} onValueChange={(v) => setForm((f) => ({ ...f, responsible_user_id: v }))}>
            <SelectTrigger data-testid="equipment-issue-edit-responsible"><SelectValue /></SelectTrigger>
            <SelectContent>
              {!issue.responsible_candidates.some((c) => c.id === issue.responsible_user_id) && <SelectItem value={issue.responsible_user_id}>{issue.responsible_name}</SelectItem>}
              {issue.responsible_candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select></div>
      )}
      <div className="space-y-1.5"><Label htmlFor="eq-edit-symptoms">Symptômes</Label>
        <Textarea id="eq-edit-symptoms" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.symptoms} onChange={set("symptoms")} /></div>
      <div className="space-y-1.5"><Label htmlFor="eq-edit-impact">Impact</Label>
        <Textarea id="eq-edit-impact" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.impact} onChange={set("impact")} /></div>
      <div className="space-y-1.5"><Label htmlFor="eq-edit-action">Action tentée</Label>
        <Textarea id="eq-edit-action" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.attempted_action} onChange={set("attempted_action")} /></div>
      {[form.title, form.symptoms, form.impact, form.attempted_action].some(looksLikeSecret) && <p className="text-xs text-amber-800">Ne notez jamais de mot de passe, code ou clé Wi-Fi.</p>}
      {form.severity === "critical" && issue.severity !== "critical" && <p className="text-xs text-red-700">Le responsable et les gestionnaires du matériel seront prévenus par WhatsApp.</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button onClick={save} disabled={busy || !Object.keys(changes).length || !form.title.trim()} data-testid="equipment-issue-edit-save">
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
        </Button>
      </div>
    </div>
  );
}
